import React, { useState, useRef, useEffect } from "react";
import { streamSSE, api } from "@/lib/api";
import Markdown from "@/components/Markdown";
import { CHAT } from "@/constants/testIds";
import { toast } from "sonner";
import { Send, Plus, Sparkles, User, Loader2, Anchor } from "lucide-react";

const SUGGESTIONS = [
  "What are the key differences between a 510(k) and a De Novo submission?",
  "Explain 21 CFR 820.30 design control requirements in plain language.",
  "How do I structure a risk management file per ISO 14971:2019?",
  "What's needed for a Class II software-only device 510(k)?",
];

export default function ResearchChat() {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);   // {role, content}
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const scrollRef = useRef(null);
  const streamBufRef = useRef("");

  useEffect(() => {
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const activeProject = projects.find((p) => p.id === projectId);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || streaming) return;
    setInput("");
    const newMessages = [...messages, { role: "user", content: message }];
    setMessages(newMessages);
    setStreaming(true);
    streamBufRef.current = "";
    // append placeholder for assistant
    setMessages((m) => [...m, { role: "assistant", content: "" }]);

    try {
      await streamSSE(
        "/chat/stream",
        { session_id: sessionId, message, project_id: projectId || null },
        (frame) => {
          if (frame.session_id && !sessionId) setSessionId(frame.session_id);
          if (frame.delta) {
            streamBufRef.current += frame.delta;
            const snapshot = streamBufRef.current;
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: snapshot };
              return copy;
            });
          }
          if (frame.error) toast.error(frame.error);
        }
      );
    } catch (e) {
      toast.error("Chat error: " + e.message);
    } finally {
      setStreaming(false);
    }
  };

  const newChat = () => {
    setSessionId(null);
    setMessages([]);
    setInput("");
  };

  return (
    <div className="flex h-[calc(100vh-160px)] flex-col">
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <div className="overline mb-2 text-slate-500">RESEARCH · Claude Sonnet 4.5</div>
          <h1 className="font-serif text-3xl font-semibold tracking-tight lg:text-4xl">
            Regulatory research copilot.
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {projects.length > 0 && (
            <div className="flex items-center gap-2">
              <Anchor className="h-3.5 w-3.5 text-[#93C5FD]" />
              <select
                data-testid="chat-project-select"
                value={projectId}
                onChange={(e) => { setProjectId(e.target.value); setSessionId(null); setMessages([]); }}
                className="border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 focus:border-[#2563EB] focus:outline-none"
              >
                <option value="">No context · general chat</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.product_type === "drug" ? "℞" : "◆"} {p.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <button
            data-testid={CHAT.newBtn}
            onClick={newChat}
            className="inline-flex items-center gap-2 border border-slate-200 px-3 py-2 text-xs font-medium tracking-wide text-slate-600 transition-all hover:border-slate-300 hover:text-slate-900"
          >
            <Plus className="h-3.5 w-3.5" /> New chat
          </button>
        </div>
      </div>

      {activeProject && (
        <div data-testid="chat-context-banner" className="mb-4 flex items-center gap-3 border border-[#2563EB]/40 bg-[#2563EB]/10 px-4 py-2 text-xs">
          <Anchor className="h-3.5 w-3.5 text-[#93C5FD]" />
          <div className="text-slate-700">
            Answers scoped to <b className="text-slate-900">{activeProject.name}</b>
            {activeProject.device_name && <span className="text-slate-500"> · {activeProject.device_name}</span>}
            <span className="ml-2 border border-slate-200 px-1.5 py-0.5 font-mono text-[10px] uppercase text-slate-500">
              {activeProject.product_type === "drug" ? "DRUG · CDER" : "DEVICE · CDRH"}
            </span>
          </div>
        </div>
      )}

      {/* Chat body */}
      <div className="flex flex-1 flex-col border border-slate-200 bg-white">
        <div
          ref={scrollRef}
          data-testid={CHAT.messagesList}
          className="flex-1 space-y-6 overflow-y-auto p-6 lg:p-8"
        >
          {messages.length === 0 && (
            <div className="mx-auto max-w-2xl py-10">
              <div className="mb-8 text-center">
                <Sparkles className="mx-auto mb-4 h-8 w-8 text-[#2563EB]" strokeWidth={1.5} />
                <h3 className="font-serif text-2xl font-semibold">Ask anything about FDA regulations.</h3>
                <p className="mt-2 text-sm text-slate-500">
                  21 CFR 820, ISO 14971, IEC 62304, guidance documents, submission pathways.
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    data-testid={CHAT.suggestionCard}
                    onClick={() => send(s)}
                    className="border border-slate-200 bg-[#FAFAF7] p-4 text-left text-sm leading-relaxed text-white/75 transition-all hover:border-[#2563EB]/50 hover:bg-slate-50 hover:text-slate-900"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <MessageBubble key={i} role={m.role} content={m.content} streaming={streaming && i === messages.length - 1 && m.role === "assistant"} />
          ))}
        </div>

        {/* Input */}
        <div className="border-t border-slate-200 bg-[#FAFAF7] p-4">
          <div className="flex items-end gap-3">
            <textarea
              data-testid={CHAT.input}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="Ask about a regulation, guidance doc, submission pathway…"
              rows={1}
              className="flex-1 resize-none border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#2563EB] focus:outline-none"
            />
            <button
              data-testid={CHAT.sendBtn}
              disabled={!input.trim() || streaming}
              onClick={() => send()}
              className="inline-flex h-[46px] shrink-0 items-center gap-2 bg-[#2563EB] px-4 text-sm font-semibold text-white transition-all hover:bg-[#1D4ED8] disabled:opacity-40"
            >
              {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </div>
          <div className="overline mt-2 text-slate-300">
            AI-generated · Verify with qualified regulatory counsel · Not legal advice
          </div>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ role, content, streaming }) {
  if (role === "user") {
    return (
      <div className="flex justify-end">
        <div className="flex max-w-[80%] items-start gap-3">
          <div className="border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800">
            {content}
          </div>
          <div className="flex h-8 w-8 shrink-0 items-center justify-center border border-slate-200 bg-[#FAFAF7]">
            <User className="h-3.5 w-3.5 text-slate-600" />
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-3">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center border border-[#2563EB]/50 bg-[#2563EB]/10">
        <Sparkles className="h-3.5 w-3.5 text-[#2563EB]" />
      </div>
      <div className="min-w-0 flex-1">
        {content ? <Markdown>{content}</Markdown> : (
          <div className="flex items-center gap-1.5 py-2">
            <span className="typing-dot" />
            <span className="typing-dot" />
            <span className="typing-dot" />
          </div>
        )}
        {streaming && content && (
          <div className="mt-1 inline-flex items-center gap-1">
            <span className="typing-dot" />
            <span className="typing-dot" />
            <span className="typing-dot" />
          </div>
        )}
      </div>
    </div>
  );
}
